/**
 * One payslip, laid out to be read on screen and printed on paper.
 *
 * The content is driven by Fair Work's payslip requirements rather than by what
 * looked tidy: employer name and ABN, the employee, the pay period and the
 * issue date, each rate and the hours at it, gross, tax withheld, net, and the
 * superannuation contribution with the fund it is going to. Anything the API
 * has not sent is rendered as a visible gap rather than quietly omitted, so a
 * missing ABN is something a boss can see and fix instead of a compliance hole
 * nobody noticed.
 */
import { createPortal } from 'react-dom';
import type { Payslip } from '../lib/api.ts';
import { fmtDateMedium, fmtDateRange, fmtDateTime12, fmtHours, fmtMoney } from '../lib/time.ts';
import { PandaMark } from './Bits.tsx';

const n = (v: string | number | null | undefined) => {
  const x = typeof v === 'string' ? Number(v) : (v ?? 0);
  return Number.isFinite(x) ? x : 0;
};

function Missing({ what }: { what: string }) {
  return (
    <span className="text-[12px] font-semibold" style={{ color: 'var(--sp-warn-strong)' }}>
      {what} not set
    </span>
  );
}

export function PayslipSheet({ slip, staffName }: { slip: Payslip; staffName: string }) {
  const lines = slip.lines ?? [];
  return (
    <article className="print-sheet mx-auto w-full" style={{ maxWidth: 720 }}>
      <header className="flex items-start justify-between gap-4 pb-4" style={{ borderBottom: '2px solid var(--sp-text)' }}>
        <div className="flex items-start gap-3">
          <PandaMark size={40} />
          <div>
            <h1 className="text-[17px] font-extrabold leading-tight text-ink">
              {slip.tenantName ?? <Missing what="Business name" />}
            </h1>
            <p className="text-[12.5px] leading-snug text-muted">
              {slip.tenantAbn ? `ABN ${slip.tenantAbn}` : <Missing what="ABN" />}
            </p>
            {slip.tenantAddress ? (
              <p className="text-[12.5px] leading-snug text-muted">{slip.tenantAddress}</p>
            ) : null}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Payslip</div>
          <div className="text-[13px] font-semibold text-ink">
            {slip.periodStart && slip.periodEnd ? fmtDateRange(slip.periodStart, slip.periodEnd) : 'Pay period'}
          </div>
          <div className="text-[12px] text-muted">
            Issued {slip.issuedAt ? fmtDateTime12(slip.issuedAt) : fmtDateMedium(new Date().toISOString().slice(0, 10))}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4 py-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Paid to</div>
          <div className="text-[15px] font-bold text-ink">{staffName}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Net pay</div>
          <div className="tabular text-[24px] font-extrabold leading-tight" style={{ color: 'var(--sp-accent-strong)' }}>
            {fmtMoney(slip.net)}
          </div>
        </div>
      </div>

      <table className="w-full" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderTop: '1px solid var(--sp-border)', borderBottom: '1px solid var(--sp-border)' }}>
            <th className="py-2 text-left text-[11px] font-bold uppercase tracking-wide text-faint">Description</th>
            <th className="py-2 text-right text-[11px] font-bold uppercase tracking-wide text-faint">Hours</th>
            <th className="py-2 text-right text-[11px] font-bold uppercase tracking-wide text-faint">Rate</th>
            <th className="py-2 text-right text-[11px] font-bold uppercase tracking-wide text-faint">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={4} className="py-4 text-center text-[13px] text-muted">
                No itemised lines were recorded for this payslip.
              </td>
            </tr>
          ) : (
            lines.map((l, i) => (
              <tr key={l.id ?? `${l.bucket}-${i}`} style={{ borderBottom: '1px solid var(--sp-hairline)' }}>
                <td className="py-2 text-[13.5px] text-ink">{l.label}</td>
                <td className="tabular py-2 text-right text-[13.5px] text-ink">{fmtHours(n(l.hours) * 60)}</td>
                <td className="tabular py-2 text-right text-[13.5px] text-ink">{fmtMoney(l.rate)}</td>
                <td className="tabular py-2 text-right text-[13.5px] font-semibold text-ink">{fmtMoney(l.amount)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="ml-auto mt-4" style={{ maxWidth: 320 }}>
        {[
          { label: 'Gross pay', value: fmtMoney(slip.gross), strong: false },
          { label: 'PAYG withholding', value: `-${fmtMoney(slip.tax)}`, strong: false },
          { label: 'Net pay', value: fmtMoney(slip.net), strong: true },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between py-1.5"
            style={row.strong ? { borderTop: '2px solid var(--sp-text)', marginTop: 4, paddingTop: 8 } : undefined}
          >
            <span className={row.strong ? 'text-[14px] font-bold text-ink' : 'text-[13px] text-muted'}>
              {row.label}
            </span>
            <span
              className={`tabular ${row.strong ? 'text-[17px] font-extrabold text-ink' : 'text-[13.5px] font-semibold text-ink'}`}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <section
        className="mt-5 rounded-card border p-4"
        style={{ background: 'var(--sp-surface-2)', borderColor: 'var(--sp-border)' }}
      >
        <div className="flex items-baseline justify-between">
          <span className="text-[13px] font-bold text-ink">Superannuation</span>
          <span className="tabular text-[15px] font-extrabold text-ink">{fmtMoney(slip.superAmount)}</span>
        </div>
        <p className="mt-1 text-[12.5px] leading-relaxed text-muted">
          Paid on top of your wage, not deducted from it.
          {slip.superFundName ? ` To ${slip.superFundName}` : ''}
          {slip.superMemberNumber ? `, member ${slip.superMemberNumber}` : ''}
          {slip.superFundName ? '.' : ' Fund details are not on file yet.'}
        </p>
      </section>

      <section className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Year to date</div>
        <div className="mt-1 grid grid-cols-3 gap-3">
          {[
            { label: 'Gross', value: slip.ytdGross },
            { label: 'Tax withheld', value: slip.ytdTax },
            { label: 'Super', value: slip.ytdSuper },
          ].map((row) => (
            <div key={row.label}>
              <div className="text-[12px] text-muted">{row.label}</div>
              <div className="tabular text-[14px] font-bold text-ink">{fmtMoney(row.value)}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="mt-6 pt-3 text-[11.5px] leading-relaxed text-faint" style={{ borderTop: '1px solid var(--sp-hairline)' }}>
        Keep this payslip for your records. If anything looks wrong, tell your manager before the next pay run and
        it can be corrected.
      </footer>
    </article>
  );
}

/**
 * The print portal. Mounted on <body> rather than inside the app tree, because
 * printing then only has to hide #root and show this one node instead of
 * fighting a drawer's stacking and overflow contexts.
 */
export function PayslipPrintArea({ slip, staffName }: { slip: Payslip | null; staffName: string }) {
  if (!slip) return null;
  return createPortal(
    <div className="print-area">
      <PayslipSheet slip={slip} staffName={staffName} />
    </div>,
    document.body,
  );
}
